import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Infer } from 'superstruct';

import { createSuccessToast, createToast } from '@adflow/frontend/ui-toasts';
import { apiRequest, handleRequestErrors } from '@adflow/frontend/util-query';
import {
	GetSalesforceImportPreview,
	GetSalesforceImportSelectionOptions,
	StartSalesforceImport,
} from '@adflow/shared/data-salesforce-import';

const salesforceImportKeys = {
	all: ['salesforce-import'],

	selectionOptions: () =>
		[...salesforceImportKeys.all, 'selection-options'] as const,
	preview: (onGroup?: string, onCampaignName?: string) =>
		[...salesforceImportKeys.all, 'preview', onGroup, onCampaignName] as const,
};

export function useSalesforceImportSelectionOptions(
	onGroup?: string,
	enabled = true,
	onType?: string,
	startDate?: string,
	endDate?: string,
	onCampaignName?: string
) {
	return useQuery({
		enabled,
		queryKey: [
			...salesforceImportKeys.selectionOptions(),
			startDate,
			endDate,
			onGroup,
			onType,
			onCampaignName,
		],
		queryFn: ({ signal }) =>
			apiRequest(
				GetSalesforceImportSelectionOptions,
				{
					query: {
						startDate,
						endDate,
						onGroup,
						onType,
						onCampaignName,
					},
				},
				{ signal }
			),
	});
}

export function useSalesforceImportPreview(
	{
		onGroup,
		onCampaignName,
	}: {
		onGroup?: string;
		onCampaignName?: string;
	},
	enabled = true
) {
	return useQuery({
		enabled: enabled && Boolean(onGroup && onCampaignName),
		queryKey: salesforceImportKeys.preview(onGroup, onCampaignName),
		queryFn: ({ signal }) =>
			apiRequest(
				GetSalesforceImportPreview,
				{
					query: {
						onGroup: onGroup ?? '',
						onCampaignName: onCampaignName ?? '',
					},
				},
				{ signal }
			),
	});
}

type StartSalesforceImport = Infer<typeof StartSalesforceImport.req>;

export function useSalesforceStartImportMutation() {
	const { t } = useTranslation('salesforce-import');
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: StartSalesforceImport['body']) =>
			apiRequest(StartSalesforceImport, { body }),
		onSuccess(data) {
			if (data.facebookAmount > 0) {
				void queryClient.invalidateQueries({
					queryKey: ['facebook', 'template', 'list'],
				});
			}

			if (data.googleAmount > 0) {
				void queryClient.invalidateQueries({
					queryKey: ['google-ads', 'template', 'list'],
				});
			}

			void queryClient.invalidateQueries({
				queryKey: salesforceImportKeys.all,
			});

			if (data.facebookAmount + data.googleAmount === 0) {
				createToast(
					() => t('importSalesforceTemplatesNoResultsTitle'),
					t('importSalesforceTemplatesNoResultsMessage'),
					'warning'
				);
				return;
			}

			createSuccessToast(
				t('importSalesforceTemplatesSuccessMessage', {
					count: data.facebookAmount + data.googleAmount,
				})
			);
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('error.loadingSalesforceTemplates'),
			});
		},
	});
}
